import { useState, useEffect, useMemo } from 'preact/hooks'
import clsx from 'clsx'
import { addCartItem, clearBundlesChildCart, getCart, getMaxQuantityByVariant } from 'libs/cart'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { PRODUCT_TYPES } from 'helpers/constants'
import ButtonATC from 'snippets/button-atc/button-atc'
import QuantityInput from 'snippets/form/quantity-input/quantity-input'
import ProductStatus from 'snippets/product/product-status/product-status'
import WishlistButton from 'snippets/wishlist/wishlist-button/wishlist-button'

function ProductATC({
  title,
  currentVariant,
  buttonText,
  initProduct,
  buttonClass = 'w-full',
  isEnabledInput = true,
  classWrap = '',
  currentVariantPrice = currentVariant.price,
  sellingPlanId = null,
  productBundles = [],
  showWishlistButton = false,
  hiddenPrice = false
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [cartItems, setCartItems] = useState(GM_STATE.cart.initCart.items)
  const [maxQuantity, setMaxQuantity] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reRenderKey, setReRenderKey] = useState(1)

  const { id, available } = currentVariant
  const isDisabled = useMemo(() => quantity > maxQuantity, [quantity, maxQuantity])

  const getMaxQuantity = async (newCart, variant) => {
    setCartItems(newCart.items)

    return getMaxQuantityByVariant(newCart.items, variant)
  }

  const syncMaxQuantity = async (newCart) => {
    setIsLoading(true)

    const maxValue = await getMaxQuantity(newCart, currentVariant)

    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const resetQuantity = (value = 1) => {
    setQuantity(value)
    setReRenderKey(reRenderKey + 1)
  }

  const addToCart = async () => {
    try {
      const itemsCart = GM_STATE.cart.initCart.items
      const isProductBundle = initProduct.type === PRODUCT_TYPES.VALUE_BUNDLE

      setIsAdding(true)

      if (isProductBundle && itemsCart.length && productBundles.length) {
        await clearBundlesChildCart(itemsCart, initProduct.id, productBundles)
      }

      const properties = {}
      const response = await addCartItem(id, properties, quantity, sellingPlanId)

      if (!response) throw new Error('Can not add to cart')

      const newCart = await getCart()

      globalEmitter.emit(emitterProps.cart.add, newCart)

      const max = getMaxQuantityByVariant(newCart.items, currentVariant)

      resetQuantity(max > 0 && max < quantity ? max : 1)
      await syncMaxQuantity(newCart)
    } catch (error) {
      console.error(error)
    } finally {
      setIsAdding(false)
    }
  }

  const updateMaxQuantity = (items) => {
    setIsLoading(true)

    const maxValue = getMaxQuantityByVariant(items, currentVariant)

    setCartItems(items)
    setMaxQuantity(maxValue)
    setIsLoading(false)
  }

  const cartUpdateListener = (items) => updateMaxQuantity(items, currentVariant)

  useEffect(() => {
    if (currentVariant) {
      resetQuantity(1)
      updateMaxQuantity(cartItems, currentVariant)
      globalEmitter.on(emitterProps.cart.update, cartUpdateListener)
    }

    return () => {
      globalEmitter.off(emitterProps.cart.update, cartUpdateListener)
    }
  }, [currentVariant])

  return (
    <>
      <div className={classWrap}>
        {isEnabledInput && (
          <div className="mb-4 flex items-start md:mb-6">
            <div className={`flex flex-col gap-1 ${!(available && maxQuantity > 0) ? 'pointer-events-none' : ''}`}>
              <QuantityInput
                key={reRenderKey}
                quantity={quantity}
                min={1}
                max={maxQuantity}
                setQuantity={setQuantity}
                isLoading={isLoading}
                classWrap="min-h-12 !p-0 w-24"
                classButton="w-9 h-11"
                title={title}
              />
            </div>
            <ProductStatus maxQuantity={maxQuantity} isLoading={isLoading} />
          </div>
        )}
        <div className="flex flex-row">
          <ButtonATC
            disabled={!available || maxQuantity <= 0 || isDisabled}
            text={available ? buttonText : translate.product.soldOut}
            onHandle={addToCart}
            price={currentVariantPrice}
            loading={isAdding}
            buttonClass={buttonClass}
            hiddenPrice={hiddenPrice || !available}
          />
        </div>
      </div>
      {showWishlistButton && available && (
        <WishlistButton
          className={clsx('min-h-[46px] flex items-center justify-center border w-[62px] disabled:border-neutral-100 disabled:text-neutral-500', isDisabled ? 'border-neutral-100 text-tooltip' : 'border-neutral-900 text-neutral-900')}
          classIcon="size-4 text-inherit"
          product={initProduct}
          variantSelected={currentVariant?.id}
          isProductForm
          disabled={isDisabled}
        />
      )}
    </>
  )
}

export default ProductATC
